// ============================================
// useAiChatServer — 씬 클라이언트 훅 (C′ 통합설계 §8, ai_manager_refact)
// ============================================
// 에이전트 루프는 node-agent 서버가 실행 — 브라우저는 질문 접수(POST api3/ai-agent/run/accept) 후
// 결과를 짧게 반복 조회(GET api3/ai-agent/run/{turnId})한다. 연결을 오래 붙잡지 않아 재배포에도 답을 잃지 않는다.
// useAiChat(브라우저 루프)과 동일 반환 인터페이스 유지 → AiChattingView 등 기존 컴포넌트 무수정 재사용.
// OpenAI 키·에이전트 세션·버퍼·텔레메트리는 전부 서버 소유. 세션 목록/복원/평가/SMS 발송은 기존 Spring API 직접 호출 유지.

import { useState, useEffect, useRef, useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';

import FetchModule from '../../Model/Share/Network/FetchModule';
import { RESPONSE_FAILED_RETRY, CANCELLED_RESPONSE, TOOL_CALLING_LOG } from '../../AiManager/Model/aiConfig';
import { createSmsHandlers, restoreSmsFromSave } from '../../AiManager/Transaction/smsHandlers';
import { detectResponseLanguage } from '../../Agent/Tools/responseLanguage';

// 폴링 간격 — 첫 15초는 1초, 그 뒤로는 3초 //
// 턴 진행 기록(「살펴본 과정」)은 단계가 바뀔 때만 서버에 적힌다. 1·3·6·9초 간격이면 트리아지(평균 2.6초) 전환이
// 턴의 절반가량에서 6초에야 보이고, 답이 끝난 뒤 화면에 뜨기까지도 평균 1.5초가 더 걸렸다. 조회는 PK 한 건이라 가볍다 //
const POLL_FAST_WINDOW_MS = 15 * 1000;
const POLL_FAST_INTERVAL_MS = 1000;
const POLL_SLOW_INTERVAL_MS = 3000;

// 폴링 총 대기 상한 — 턴 하드 상한 330 + 텔레메트리 저장 최악 95 + 여유 10.
// 백엔드 API3AiAgentService.RUNNING_STALE_SECONDS와 같은 값이라 서버도 이 시점에 failed로 판정한다.
// 서버가 AGENT_TURN_BUDGET_MS / TURN_TIMEOUT_MS를 바꾸면 이 값도 함께 바꾼다 //
const POLL_TIMEOUT_MS = 435 * 1000;

// 토큰 소진 안내 — useAiChat.js 동일 문구 (dual sync) //
const TOKEN_EXHAUSTED_MESSAGE = '이번 달 AI 토큰을 모두 사용했어요. 다음 달에 다시 이용하시거나 토큰을 충전해 주세요.';

// 로딩 문구 — 언어별 단계 문구를 경과 시간에 따라 넘긴다. shimmer 는 그대로 진행 신호를 맡는다 //
// 2026-09-23 교체: 랜덤 8종("잠깐만요!" 등)은 제출 시 한 번 뽑혀 턴 내내(평균 6.6초) 고정돼 있었다.
//   ChatGPT·Gemini 웹 실측 — 랜덤·감탄형 문구 없이 짧은 진행 문구를 애니메이션과 함께 둔다(Gemini 는 5.5초 동안 같은 줄).
//   에이전트는 사용자 말투와 무관하게 존댓말이 기본이라 반말 세트는 두지 않는다.
//   여기서 레거시 useAiChat LOADING_MESSAGES 와의 dual sync 를 끊는다 — 레거시는 컷오버로 사라질 경로라 따라가지 않는다.
//
// 경과 시간에 따라 다음 줄로 넘긴다. 진행 단계를 지어내지 않고(실제 단계는 서버만 안다) 어느 턴에나 참인 말만 쓴다.
//   4초  — 턴의 73%가 4초를 넘는다(신경로 22턴, 평균 6.6초). 대기 중 변화가 보이게 하는 줄이라 "느리다"는 말은 넣지 않는다.
//   10초 — 하위 9%만 여기 온다. 사용자가 주의를 잃기 시작하는 한계라 "시간이 걸린다"는 안내는 여기서부터.
//   30초 — 관측 최대(15.7초)의 약 2배. 실제로 느린 상황이라 그 사실을 말한다.
//   "거의 다 됐어요"처럼 끝나는 시점이나 원인을 단정하는 말은 쓰지 않는다 — 클라이언트는 그걸 모른다.
const LOADING_STAGES = {
  ko: [
    { afterMs: 0, text: '생각하고 있어요' },
    { afterMs: 4000, text: '답변을 준비하고 있어요' },
    { afterMs: 10000, text: '시간이 조금 걸리고 있어요' },
    { afterMs: 30000, text: '평소보다 오래 걸리고 있어요. 잠시만 기다려 주세요' },
  ],
  en: [
    { afterMs: 0, text: 'Thinking it over' },
    { afterMs: 4000, text: 'Preparing your answer' },
    { afterMs: 10000, text: 'This is taking a little longer' },
    { afterMs: 30000, text: 'Taking longer than usual. Please hold on' },
  ],
  ja: [
    { afterMs: 0, text: '考えています' },
    { afterMs: 4000, text: '回答を準備しています' },
    { afterMs: 10000, text: '少し時間がかかっています' },
    { afterMs: 30000, text: 'いつもより時間がかかっています。少々お待ちください' },
  ],
};

// 단계 전환 확인 주기 — 폴링(1·3·6·9초)에 묶으면 4초 전환이 6초에 일어나서 따로 돈다 //
const LOADING_TICK_MS = 500;

// 턴 진행 기록 JSON(서버가 조립한 [{ key, state, domains }]) → 배열. 형식이 틀리거나 비었으면 null //
// 답을 기다리는 동안만 쓴다(말풍선 밖 진행 줄). 문장은 AiProgressTrail 이 언어별로 만든다 — 여기서는 모양만 거른다 //
function parseProgress(progressJson) {
  if (!progressJson) return null;

  try {
    const parsed = JSON.parse(progressJson);

    if (!Array.isArray(parsed)) return null;

    const items = parsed.filter(item => item && typeof item.key === 'string' && typeof item.state === 'string');

    return items.length > 0 ? items : null;
  }
  catch {
    return null;
  }
}

// renderJson(화면 재현 정본) → 메시지 채널 플래그 //
// 서버는 표/차트/SMS를 개별 필드가 아니라 renderJson 문자열 하나로 내려준다(displayChannels.buildRenderJson).
// renderJson 없이 저장된 턴은 finalText 평문이므로 파싱 실패는 채널 없음으로 떨어뜨린다 //
function parseRenderJson(renderJson) {
  if (!renderJson) return {};

  try {
    const parsed = JSON.parse(renderJson);

    if (parsed?.text === undefined) return {};

    // salesViewData는 weeksData(렌더 페이로드) 있을 때만 부착 — 코어 v1 요약판은 로그/검증용이라 미부착 (SalesViewRenderer:38 weeksData.map 무가드 크래시 차단) //
    return {
      ...(parsed.displayItems ? { displayItems: parsed.displayItems } : {}),
      ...(Array.isArray(parsed.extraDisplays) && parsed.extraDisplays.length > 0 ? { extraDisplays: parsed.extraDisplays } : {}),
      ...(parsed.vizData ? { vizData: parsed.vizData } : {}),
      ...(parsed.salesViewData?.weeksData ? { salesViewData: parsed.salesViewData } : {}),
      ...(parsed.revenueChartData ? { revenueChartData: parsed.revenueChartData } : {}),
      ...(parsed.smsRecommendation ? { smsRecommendation: restoreSmsFromSave(parsed.smsRecommendation) } : {}),
    };
  }
  catch {
    return {};
  }
}

// renderJson 의 화면 글 — 신 경로 서버는 finalText(평문 원본)와 따로, 화면용 서식본(문단·수치 굵게·목록)을 여기에 싣는다.
// 없거나(구 서버·renderJson 없는 턴) 비면 null — 호출부가 finalText 로 떨어진다 //
function renderJsonText(renderJson) {
  if (!renderJson) return null;

  try {
    const text = JSON.parse(renderJson)?.text;

    return typeof text === 'string' && text.trim() ? text : null;
  }
  catch {
    return null;
  }
}

export default function useAiChatServer() {
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [isResponding, setIsResponding] = useState(false);
  const [tokenExhausted, setTokenExhausted] = useState(false);
  const [usagePercent, setUsagePercent] = useState(0);
  const [storeName, setStoreName] = useState('');

  const [sessions, setSessions] = useState([]);
  const [sessionId, setSessionId] = useState(`session_${uuidv4()}`);
  const [messageRatings, setMessageRatings] = useState({});

  const chatMessagesContainerRef = useRef(null);
  const chatMessagesRef = useRef(chatMessages);
  chatMessagesRef.current = chatMessages;

  // 메시지별 대화 저장 메타 { [messageId]: { conversationId, userInput, intention, elapsedMs } } //
  const conversationMetaRef = useRef({});

  // 진행 중인 턴 — { cancelled, ticker }. 턴마다 새 객체를 만든다 //
  // 종전에는 중단 여부·로딩 타이머를 훅 전체가 ref 하나로 공유했다. 중단 직후 다시 질문하면 새 턴이 그 값을 되돌려
  // 잠들어 있던 옛 폴링이 되살아났고, 옛 턴의 마무리(finally)가 새 턴의 타이머를 끄고 입력창을 열었다.
  // 턴별 객체면 옛 턴은 제 객체의 cancelled 만 보므로 새 턴과 섞이지 않는다 //
  const activeTurnRef = useRef(null);
  const tokenExhaustedRef = useRef(false);

  // ============================================
  // 사용량 게이지 초기화 — 권한 조회 (키 복호화 없음: 키는 서버 소유) //
  // ============================================
  useEffect(() => {
    const fetchModule = new FetchModule();
    (
      async () => {
        try {
          const responseData = await fetchModule.getDataVersion2('ai_manage/permission', 'GET');

          if (responseData.status === 200 && responseData.data) {
            const data = responseData.data;

            setStoreName(data.storeName || '');
            if (typeof data.usagePercent === 'number') setUsagePercent(data.usagePercent);

            if (data.tokenExhausted === true) {
              setUsagePercent(100);
              tokenExhaustedRef.current = true;
              setTokenExhausted(true);
            }
          }
        }
        catch (error) {
          if (TOOL_CALLING_LOG) console.error('[AgentServer] permission error:', error);
        }
      }
    )();
  }, []);

  // ============================================
  // 채팅 스크롤 처리 (useAiChat 스페이서 로직 동일 — dual sync) //
  // ============================================
  const scrollTargetIdRef = useRef(null);
  const activeAiMsgIdRef = useRef(null);
  const initialSpacerHeightRef = useRef(0);

  function resetSpacer() {
    activeAiMsgIdRef.current = null;
    initialSpacerHeightRef.current = 0;
    const container = chatMessagesContainerRef.current;
    if (container) container.style.setProperty('--after-spacer-height', '0px');
  }

  useEffect(() => {
    // 1. 사용자 메시지 전송 직후 → 빈공간 생성 + 상단 스크롤
    const targetId = scrollTargetIdRef.current;
    if (targetId) {
      scrollTargetIdRef.current = null;
      const container = chatMessagesContainerRef.current;
      if (container) {
        const spacer = container.clientHeight;
        initialSpacerHeightRef.current = spacer;
        container.style.setProperty('--after-spacer-height', `${spacer}px`);
        requestAnimationFrame(() => {
          const target = container.querySelector(`[data-message-id="${targetId}"]`);
          if (target) {
            target.scrollIntoView({ block: 'start', behavior: 'smooth' });
          }
        });
      }
      return;
    }

    // 2. AI 응답 타이핑 중 → 응답 높이만큼 빈공간 차감
    if (activeAiMsgIdRef.current && initialSpacerHeightRef.current > 0) {
      const container = chatMessagesContainerRef.current;
      if (container) {
        const aiMsgEl = container.querySelector(`[data-message-id="${activeAiMsgIdRef.current}"]`);
        const aiHeight = aiMsgEl ? aiMsgEl.offsetHeight : 0;
        const spacerHeight = Math.max(0, initialSpacerHeightRef.current - aiHeight);
        container.style.setProperty('--after-spacer-height', `${spacerHeight}px`);
      }
    }
  }, [chatMessages]);

  // 3. 응답 종료 시 → 빈공간 제거 //
  useEffect(() => {
    if (!isResponding) resetSpacer();
  }, [isResponding]);

  // 4. 화면 리사이즈 시 → 빈공간 제거 //
  useEffect(() => {
    function onResize() { resetSpacer(); }
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // 5. 언마운트 cleanup — 진행 중 폴링 중단 //
  useEffect(() => {
    return () => {
      const turn = activeTurnRef.current;

      if (turn) {
        turn.cancelled = true;
        stopLoadingTicker(turn);
      }
    };
  }, []);

  // ============================================
  // 채팅 메시지 관리 (추가/업데이트 — useAiChat 동일) //
  // ============================================
  // 사용자 메시지 추가 //
  function addUserMessage(text) {
    const id = `msg_${uuidv4()}`;
    setChatMessages(prev => [...prev, { id, text, isAi: false, timestamp: Date.now() }]);
    return id;
  }

  // AI 메시지 추가 — richText: 답 글을 마크다운 + 한 줄 바꿈 보존으로 그린다(AnswerMarkdown). 레거시 /ai 훅은 이 값이 없다 //
  function addAiMessage(text = '') {
    const id = `msg_${uuidv4()}`;
    setChatMessages(prev => [...prev, { id, text, isAi: true, richText: true, timestamp: Date.now() }]);
    return id;
  }

  // AI 메시지 업데이트 (flags 값이 함수이면 이전 값을 인자로 전달하여 부분 업데이트) //
  function updateAiMessage(id, text, flags = {}) {
    setChatMessages(prev => prev.map(msg => {
      if (msg.id !== id) return msg;

      const resolved = {};
      for (const [key, val] of Object.entries(flags)) {
        resolved[key] = typeof val === 'function' ? val(msg[key]) : val;
      }

      return {
        ...msg,
        ...(text !== undefined ? { text } : {}),
        ...resolved,
      };
    }));
  }

  // 로딩 메시지 표시 (shimmer) — 입력 언어로 문구 단계를 고르고, 경과 시간에 따라 다음 단계로 넘긴다 //
  // 로딩이 풀린 메시지(답변 타이핑·실패 안내)는 건드리지 않는다 — 타이머가 한 박자 늦게 돌아도 답을 덮지 않게 //
  function showToolLoading(id, userInput, turn) {
    const lang = detectResponseLanguage(userInput || '');
    const stages = LOADING_STAGES[lang] || LOADING_STAGES.ko;
    const startedAt = Date.now();
    let shown = 0;

    stopLoadingTicker(turn);
    updateAiMessage(id, stages[0].text, { isToolLoading: true, progressLang: lang });

    turn.ticker = setInterval(() => {
      if (turn.cancelled) {
        stopLoadingTicker(turn);
        return;
      }

      const elapsed = Date.now() - startedAt;
      let next = shown;

      while (next + 1 < stages.length && stages[next + 1].afterMs <= elapsed) next++;

      if (next === shown) return;

      shown = next;
      setChatMessages(prev => prev.map(msg => (msg.id === id && msg.isToolLoading) ? { ...msg, text: stages[next].text } : msg));

      if (shown === stages.length - 1) stopLoadingTicker(turn);
    }, LOADING_TICK_MS);
  }

  // 로딩 문구 타이머 해제 — 그 턴의 타이머만 끈다(답변 타이핑 시작·실패·중단·언마운트) //
  function stopLoadingTicker(turn) {
    if (turn?.ticker) {
      clearInterval(turn.ticker);
      turn.ticker = null;
    }
  }

  // 로딩 해제 → 일반 메시지로 전환 //
  function clearToolLoading(id, turn) {
    stopLoadingTicker(turn);
    setChatMessages(prev => prev.map(msg => msg.id === id ? { ...msg, isToolLoading: false } : msg));
  }

  // 타이핑 효과 (최종 응답을 한 글자씩 — useAiChat 동일 3자/15ms) //
  async function typeMessage(id, text, turn, chunkSize = 3, delayMs = 15) {
    clearToolLoading(id, turn);
    let current = '';
    for (let i = 0; i < text.length; i += chunkSize) {
      if (turn.cancelled) break;
      current += text.slice(i, i + chunkSize);
      updateAiMessage(id, current);
      await new Promise(r => setTimeout(r, delayMs));
    }
    if (!turn.cancelled) {
      updateAiMessage(id, text);
    }
  }

  // ============================================
  // 평가 (useAiChat 동일 — POST ai-log/rating) //
  // ============================================
  const handleRateMessage = async (messageId, score, reason = null, comment = null) => {
    const meta = conversationMetaRef.current[messageId];
    if (!meta?.conversationId) return;

    const prevRating = messageRatings[messageId] || null;

    setMessageRatings(prev => ({ ...prev, [messageId]: { score, reason, comment } }));

    const fetchModule = new FetchModule();
    let isSaved = false;

    try {
      const res = await fetchModule.postDataVersion2('ai-log/rating', 'POST', {
        conversationId: meta.conversationId,
        sessionId,
        score,
        reason,
        comment,
      });
      isSaved = res?.status === 200;
    }
    catch (error) {
      isSaved = false;
    }

    // 저장 실패 시 조용히 이전 상태로 복귀 (채팅 동작 무영향) //
    if (!isSaved) setMessageRatings(prev => ({ ...prev, [messageId]: prevRating }));
  };

  // 평가 가능 여부 — 저장된 turn(conversationId 보유)만 평가 UI 노출 //
  const isMessageRatable = (messageId) => !!conversationMetaRef.current[messageId]?.conversationId;

  // ============================================
  // 턴 실행 — 접수 후 폴링 (Spring api3 경유) //
  // ============================================
  // 턴 접수 — turnId 발급. 409는 같은 세션에 진행 중인 턴이 있다는 뜻이고,
  // 그때도 data.turnId가 오므로 그 턴을 이어서 폴링한다(중단했던 턴을 잃지 않는다) //
  async function acceptAgentRun(userInput) {
    const fetchModule = new FetchModule();
    const responseData = await fetchModule.postDataVersion3('ai-agent/run/accept', 'POST', { sessionId, input: userInput });

    if (responseData.status === 200 || responseData.status === 409) return responseData.data?.turnId || null;

    return null;
  }

  // 턴 결과 조회 — running이면 간격을 두고 다시 조회하고, 그때마다 진행 기록을 onProgress 로 넘긴다.
  // 중단(turn.cancelled)·상한(435초)·조회 실패(404 포함)는 전부 null로 마무리한다.
  // 중단은 조회 전후 둘 다 본다 — 응답을 기다리는 사이 중단돼도 그 응답으로 화면을 바꾸지 않게 //
  async function pollAgentRun(turnId, turn, onProgress) {
    const fetchModule = new FetchModule();
    const startedAt = Date.now();
    const deadline = startedAt + POLL_TIMEOUT_MS;

    while (Date.now() < deadline) {
      const intervalMs = Date.now() - startedAt < POLL_FAST_WINDOW_MS ? POLL_FAST_INTERVAL_MS : POLL_SLOW_INTERVAL_MS;

      await new Promise(r => setTimeout(r, intervalMs));

      if (turn.cancelled) return null;

      const responseData = await fetchModule.getDataVersion3(`ai-agent/run/${turnId}`, 'GET');

      if (turn.cancelled) return null;

      if (responseData.status !== 200) return null;

      if (responseData.data?.state !== 'running') return responseData.data;

      onProgress(responseData.data?.progressJson);
    }

    return null;
  }

  // 메시지 전송 //
  const handleSend = async () => {
    // 토큰 소진 — 서버 호출 없이 안내 메시지만 노출 //
    if (tokenExhaustedRef.current) {
      if (!chatInput.trim() || isResponding) return;

      setChatInput('');
      addAiMessage(TOKEN_EXHAUSTED_MESSAGE);
      return;
    }

    if (!chatInput.trim() || isResponding) return;

    const userInput = chatInput.trim();
    setChatInput('');
    setIsResponding(true);

    const turn = { cancelled: false, ticker: null };
    activeTurnRef.current = turn;

    const userMsgId = addUserMessage(userInput);
    scrollTargetIdRef.current = userMsgId;
    const aiMessageId = addAiMessage('');
    activeAiMsgIdRef.current = aiMessageId;

    // [로딩 즉시 표시] 서버 응답을 기다리지 않고 제출 직후 로딩 문구 노출 //
    showToolLoading(aiMessageId, userInput, turn);

    // 진행 기록 — 바뀐 때만 로딩 중인 메시지에 붙인다(같은 목록이면 다시 그리지 않는다) //
    let lastProgressJson = null;
    const onProgress = progressJson => {
      if (!progressJson || progressJson === lastProgressJson) return;

      const items = parseProgress(progressJson);

      if (!items) return;

      lastProgressJson = progressJson;
      setChatMessages(prev => prev.map(msg => (msg.id === aiMessageId && msg.isToolLoading) ? { ...msg, progress: items } : msg));
    };

    try {
      const turnId = await acceptAgentRun(userInput);

      if (!turnId) throw new Error('agent run accept failed');

      const data = await pollAgentRun(turnId, turn, onProgress);

      if (!data) throw new Error('agent run poll failed');

      // 실패 확정 — 서버 안내 문구를 그대로 노출 //
      if (data.state === 'failed') {
        updateAiMessage(aiMessageId, data.message || RESPONSE_FAILED_RETRY, { isToolLoading: false });

        return;
      }

      // 평가용 대화 메타 저장 — turnId가 곧 conversation_log.id(=conversationId) //
      conversationMetaRef.current[aiMessageId] = {
        conversationId: data.turnId || turnId,
        userInput,
        intention: data.intention || null,
        elapsedMs: data.elapsedMs || null,
      };

      // 사용량 게이지 갱신 + 소진 반영 //
      if (typeof data.usagePercent === 'number') setUsagePercent(data.usagePercent);

      if (data.tokenExhausted === true) {
        tokenExhaustedRef.current = true;
        setTokenExhausted(true);
        setUsagePercent(100);
      }

      const finalResponse = renderJsonText(data.renderJson) || data.finalText || (turn.cancelled ? CANCELLED_RESPONSE : RESPONSE_FAILED_RETRY);
      await typeMessage(aiMessageId, finalResponse, turn);

      // 타이핑 종료 후 표/차트/SMS 부착 — 서버는 renderJson 문자열 하나로 내려준다 //
      updateAiMessage(aiMessageId, finalResponse, {
        isToolLoading: false,
        isNewResponse: true,
        ...parseRenderJson(data.renderJson),
      });

      // 서버가 첫 턴에 세션 타이틀 저장 → 목록 갱신 //
      loadSessions();
    }
    catch (error) {
      if (TOOL_CALLING_LOG) console.error('[AgentServer] handleSend error:', error);
      const fallback = turn.cancelled ? CANCELLED_RESPONSE : RESPONSE_FAILED_RETRY;
      updateAiMessage(aiMessageId, fallback, { isToolLoading: false });
    }
    finally {
      stopLoadingTicker(turn);

      // 중단된 턴은 handleStop 이 이미 입력창을 열었다 — 그 사이 시작된 새 턴의 응답 중 상태를 끄지 않는다 //
      if (activeTurnRef.current === turn) setIsResponding(false);
    }
  };

  // 응답 중단 — 폴링만 멈춘다. 서버 턴은 계속 돌아 결과가 DB에 저장된다.
  // 같은 세션에 곧바로 다시 질문하면 접수가 409로 그 턴의 turnId를 돌려주므로 결과를 이어받는다 //
  const handleStop = () => {
    const turn = activeTurnRef.current;

    if (turn) {
      turn.cancelled = true;
      stopLoadingTicker(turn);
    }

    setIsResponding(false);
  };

  // ============================================
  // 세션 관련 함수 (useAiChat 동일 — Spring API 직접 호출) //
  // ============================================
  // 세션 목록 초기 로드 //
  useEffect(() => {
    loadSessions();
  }, []);

  // 세션 목록 불러오기 //
  const loadSessions = () => {
    const fetchModule = new FetchModule();
    (
      async () => {
        try {
          const responseData = await fetchModule.getDataVersion2('ai-chat/sessions', 'GET');

          if (responseData.status === 200) {
            setSessions(responseData.data || []);
          }
        }
        catch (error) { /* 무시 */ }
      }
    )();
  };

  // 세션 대화 내역 불러오기 — 화면 복원은 renderJson, 버퍼 복원은 서버(session-state) 책임 //
  const loadConversations = (targetSessionId) => {
    const fetchModule = new FetchModule();
    (
      async () => {
        try {
          const responseData = await fetchModule.getDataVersion2(`ai-chat/sessions/${targetSessionId}`, 'GET');

          if (responseData.status === 200 && responseData.data) {
            const messages = [];
            const restoredRatings = {};

            responseData.data.forEach(entry => {
              messages.push({ id: `msg_${uuidv4()}`, text: entry.requestMessage, isAi: false });

              // AI 응답 복원 — JSON(텍스트 + 테이블 + SMS) 또는 텍스트 원문 //
              // 진행 기록(entry.progressJson)은 화면에 그리지 않는다 — 답이 온 뒤의 「살펴본 과정」은 뺐고(2026-09-23 사용자 판단),
              // 기록은 서버가 conversation_log.progress_json 에 남겨 운영 분석용으로 쓴다 //
              let aiMsg = { id: `msg_${uuidv4()}`, isAi: true, richText: true };
              try {
                const parsed = JSON.parse(entry.responseMessage);
                if (parsed?.text !== undefined) {
                  aiMsg.text = parsed.text || '';
                  if (parsed.displayItems) aiMsg.displayItems = parsed.displayItems;
                  if (Array.isArray(parsed.extraDisplays) && parsed.extraDisplays.length > 0) aiMsg.extraDisplays = parsed.extraDisplays;
                  if (parsed.smsRecommendation) aiMsg.smsRecommendation = restoreSmsFromSave(parsed.smsRecommendation);
                  if (parsed.salesViewData) aiMsg.salesViewData = parsed.salesViewData;
                  if (parsed.revenueChartData) aiMsg.revenueChartData = parsed.revenueChartData;
                  if (parsed.vizData) aiMsg.vizData = parsed.vizData;
                }
                else {
                  aiMsg.text = entry.responseMessage;
                }
              }
              catch {
                aiMsg.text = entry.responseMessage;
              }
              messages.push(aiMsg);

              // 복원된 메시지에 대해 conversationMetaRef 설정 //
              if (entry.id) {
                conversationMetaRef.current[aiMsg.id] = {
                  conversationId: entry.id,
                  userInput: entry.requestMessage,
                  intention: entry.intention || null,
                  elapsedMs: entry.elapsedMs || null,
                };

                // 저장된 사용자평가 복원 //
                if (entry.score === 1 || entry.score === -1) {
                  restoredRatings[aiMsg.id] = {
                    score: entry.score,
                    reason: entry.reason ?? null,
                    comment: entry.comment ?? null,
                  };
                }
              }
            });

            setSessionId(targetSessionId);
            setChatMessages(messages);
            setMessageRatings(restoredRatings);
            resetSpacer();
          }
        }
        catch (error) { /* 무시 */ }
      }
    )();
  };

  // 세션 삭제 //
  const deleteSession = (targetSessionId) => {
    const fetchModule = new FetchModule();
    (
      async () => {
        try {
          const responseData = await fetchModule.getDataVersion2(`ai-chat/sessions/${targetSessionId}`, 'DELETE');

          if (responseData.status === 200) {
            if (sessionId === targetSessionId) handleNewChat();

            loadSessions();
          }
        }
        catch (error) {
          if (TOOL_CALLING_LOG) console.error('[Session] delete error:', error);
        }
      }
    )();
  };

  // 새 채팅 세션 생성 — 서버 세션은 TTL로 자연 소멸 //
  const handleNewChat = () => {
    const newSessionId = `session_${uuidv4()}`;
    setSessionId(newSessionId);
    setChatMessages([]);
    setMessageRatings({});
    resetSpacer();
  };

  // ============================================
  // SMS 핸들러 — 발송은 기존 Spring API 재사용 //
  // ============================================
  // 재작성(generate)은 LLM 필요 → clientRef null 가드로 자연 강등 (P1: 서버 위임 예정) //
  const { handleSmsSend, handleIndividualSmsSend, handleIndividualSmsGenerate } = useMemo(
    () =>
      createSmsHandlers({
        clientRef: { current: null },
        storeName,
        sessionId,
        chatMessagesRef,
        conversationMetaRef,
        updateAiMessage,
        loggedCompletion: null,
        resendTurnWithSms: null,
      }),
    [storeName, sessionId]
  );

  return {
    // 상태
    chatMessages,
    chatInput,
    isResponding,
    tokenExhausted,
    usagePercent,
    sessions,
    sessionId,
    chatMessagesContainerRef,
    messageRatings,

    // 액션
    setChatInput,
    handleSend,
    handleStop,
    handleNewChat,
    loadConversations,
    deleteSession,
    handleRateMessage,
    isMessageRatable,

    // SMS 핸들러 //
    handleSmsSend,
    handleIndividualSmsSend,
    handleIndividualSmsGenerate,
  };
}
